import { getBlockedUrlMessage } from '@/utils/urlValidation'

/**
 * Maps a raw per-website failure string to the curated, user-facing message the
 * website upload components show for a single failed website. Used for the
 * single-failure case in `applyUploadResults`, and reused for whole-job failures
 * (SSE `onError`, the initial upload request's `catch`) so those paths go through
 * the same safe/unsafe filter instead of rendering raw backend text. Anything
 * that doesn't match one of these known-safe patterns — including raw internal
 * exception text from a whole-job failure — falls back to `fallback`.
 */
export function formatWebsiteUploadError(
  error: string | undefined,
  fallback: string,
): string {
  if (!error) return fallback
  // A blocked host is valid and reachable — it just can't be read — so it must
  // not be reported as an invalid or unreachable URL.
  if (error.includes('Website URL not supported')) {
    return getBlockedUrlMessage()
  }
  if (
    error.includes('Failed to access website:') ||
    error.includes('Website URL not allowed')
  ) {
    return `${error} - Please make sure the URL is valid and publically accessible.`
  }
  if (error.includes('has already been uploaded')) {
    return `${error} - Please delete the website from the Uploaded list first.`
  }
  if (error.includes('Failed to extract')) {
    return `${error}\n This website uses protection that prevents content extraction. You can upload documents or PDFs with the same information instead.`
  }
  return fallback
}

/**
 * One line of the "several websites failed" summary, reusing the exact same
 * safe/unsafe matcher as the single-failure case above so the two can't diverge
 * again (this used to be a second, separately hand-rolled copy of the same
 * prefix-matching in each website component).
 */
export function formatWebsiteFailureLine(
  website: string,
  error: string | undefined,
): string {
  return `• ${website}: ${formatWebsiteUploadError(error, 'Sorry! Error uploading website - Please try again.')}`
}
